import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMetricsCore } from "../../../core/modules/metrics";
import { formatCurrency, formatNumber } from "../../../core/utils/formating";

interface StockAlertsWidgetProps {
  companyId: string;
}

/**
 * StockAlertsWidget Component (Independiente)
 * Muestra el resumen de las 8 métricas de alertas de stock
 * Usa los datos de Redux que se cargan en CompanyMetrics
 */
const StockAlertsWidget: React.FC<StockAlertsWidgetProps> = ({ companyId }) => {
  const navigate = useNavigate();
  const { counters, dateInit, dateEnd } = useMetricsCore();

  const handleViewMetricDetails = (metricType: string) => {
    const startDate = dateInit?.format('YYYY-MM-DD') || '';
    const endDate = dateEnd?.format('YYYY-MM-DD') || '';
    
    console.log('📅 Navegando a detalle con fechas:', { startDate, endDate, metricType });
    navigate(`/dashboard/metric-details/${companyId}/${metricType}/${startDate}/${endDate}`);
  };

  // Mapeo de nombres de métricas a tipos
  const getMetricType = (name: string): string => {
    const mapping: Record<string, string> = {
      'Productos con Stock Cero': 'STOCK_CERO',
      'Productos con Baja Rotación': 'BAJA_ROTACION',
      'Productos con Stock Crítico': 'STOCK_CRITICO',
      'Productos con Sobre Stock': 'SOBRE_STOCK',
      'Stock Cero': 'STOCK_CERO',
      'Baja Rotación': 'BAJA_ROTACION',
      'Stock Crítico': 'STOCK_CRITICO',
      'Sobre Stock': 'SOBRE_STOCK',
      'Recomendación de Compra': 'RECOMENDACION_COMPRA',
      'Compra Sugerida': 'RECOMENDACION_COMPRA',
      'Venta Sin Stock': 'VENTA_SIN_STOCK',
      'Devoluciones': 'DEVOLUCIONES',
      'Ajuste de Stock': 'AJUSTE_STOCK'
    };
    
    return mapping[name] || 'STOCK_CERO';
  };

  // Iconos por métrica
  const getMetricIcon = (name: string): string => {
    const icons: Record<string, string> = {
      'Stock Cero': '🚫',
      'Productos con Stock Cero': '🚫',
      'Baja Rotación': '🐌',
      'Productos con Baja Rotación': '🐌',
      'Stock Crítico': '⚠️',
      'Productos con Stock Crítico': '⚠️',
      'Sobre Stock': '📦',
      'Productos con Sobre Stock': '📦',
      'Recomendación de Compra': '🛒',
      'Compra Sugerida': '🛒',
      'Devoluciones': '↩️',
      'Ajuste de Stock': '🔧',
      'Venta Sin Stock': '❌',
    };
    return icons[name] || '📊';
  };

  // Semáforo de severidad
  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'low': return '#4CAF50';
      case 'medium': return '#FF9800';
      case 'high': return '#F44336';
      default: return '#4CAF50';
    }
  };

  // Empty state
  if (!counters || !Array.isArray(counters)) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body2" color="textSecondary">
          No hay métricas disponibles
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: -1.5, backgroundColor: '#f5f5f5', borderRadius: '16px', p: 1.5, pt: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
          Resumen de Alertas Diarias 📊
        </Typography>

        <Grid container spacing={2}>
          {counters.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                className="p-12 flex flex-col items-center justify-center shadow rounded-2xl"
                sx={{
                  minHeight: 110,
                  borderLeft: `5px solid ${getSeverityColor((metric as any).severity)}`,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
                  }
                }}
                onClick={() => handleViewMetricDetails(getMetricType(metric.name))}
              >
                <Typography sx={{ fontSize: 22 }}>{getMetricIcon(metric.name)}</Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 800, mt: 0.3 }}>
                  {formatNumber(metric.quantity)}
                </Typography>
                <Typography sx={{ textAlign: 'center', fontWeight: 700, fontSize: 12 }} color="text.secondary">
                  {metric.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 11 }} color="text.secondary">
                    Uds: {formatNumber(metric.amount)}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 11 }} color="text.secondary">
                    Valor: {formatCurrency(metric.price)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default StockAlertsWidget;
